//TrialSignup.jsx - For users signing up for the 21-day trial
import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { auth } from "../../config/firebase";
import {
  createUserDocument,
  hasEmailHadTrial,
} from "../../services/firestoreService";
import "./Signup.css";

const TrialSignup = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [error, setError] = useState(null);

  // Load GTM script when component mounts
  useEffect(() => {
    // Initialize dataLayer if it doesn't exist
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js'
    });

    // Create and load the GTM script
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.googletagmanager.com/gtm.js?id=GTM-WQG6RJVD';
    document.head.appendChild(script);

    // Add noscript fallback
    const noscript = document.createElement('noscript');
    const iframe = document.createElement('iframe');
    iframe.src = 'https://www.googletagmanager.com/ns.html?id=GTM-WQG6RJVD';
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noscript.appendChild(iframe);
    document.body.insertBefore(noscript, document.body.firstChild);

    // Cleanup function
    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
      if (noscript.parentNode) {
        noscript.parentNode.removeChild(noscript);
      }
    };
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Handle trial signup with enhanced security
  const handleTrialSignUp = async (e) => {
    e.preventDefault();
    setIsAuthenticating(true);
    setError(null);

    // Validation
    if (!formData.firstName.trim()) {
      setError("Please enter your first name.");
      setIsAuthenticating(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match. Please try again.");
      setIsAuthenticating(false);
      return;
    }

    if (formData.password.length < 6) {
      setError("Password must be at least 6 characters long.");
      setIsAuthenticating(false);
      return;
    }

    try {
      // Check if email has already had a trial via backend endpoint
      const isProduction = window.location.hostname.includes("run.app");
      const apiUrl = isProduction
        ? `${window.location.origin}/api/check-trial-eligibility?t=${Date.now()}`
        : "/api/check-trial-eligibility";

      const trialCheckResponse = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Cache-Control": "no-cache",
        },
        body: JSON.stringify({
          email: formData.email,
        }),
      });

      if (!trialCheckResponse.ok) {
        const errorData = await trialCheckResponse.json();
        if (trialCheckResponse.status === 403) {
          setError(
            "This email has already used a trial period. Please select a regular subscription plan.",
          );
          setIsAuthenticating(false);
          return;
        }
        throw new Error(
          errorData.error || "Failed to verify trial eligibility",
        );
      }

      // Create Firebase auth user
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password,
      );
      const user = userCredential.user;

      // Create trial subscription data
      const trialData = {
        type: "trial",
        plan: "pro",
        status: "trialing",
      };

      // Create user document with trial data
      await createUserDocument(user, formData.firstName.trim(), trialData);

      // Wait for Firestore to propagate the document
      await new Promise((resolve) => setTimeout(resolve, 2000));

      navigate("/dashboard");
    } catch (error) {
      let errorMessage = "An error occurred during sign up. Please try again.";

      if (error.code === "auth/email-already-in-use") {
        errorMessage =
          "This email is already registered. Please sign in instead.";
      } else if (error.code === "auth/invalid-email") {
        errorMessage = "Please enter a valid email address.";
      } else if (error.code === "auth/weak-password") {
        errorMessage =
          "Password is too weak. Please choose a stronger password.";
      }

      setError(errorMessage);
    } finally {
      setIsAuthenticating(false);
    }
  };

  return (
    <div className="trial-signup-container">
      {/* Left Column - Features */}
      <div className="features-column">
        <div className="features-content">
          <img
            src="/You_Create_Logo_WEB.png"
            alt="YouCreate Logo"
            className="features-logo"
          />
          <h1 className="features-heading">Your trial comes with:</h1>
          <ul className="features-list">
            <li className="feature-item">
              <span className="feature-icon">✓</span>
              21-days free (no credit card required)
            </li>
            <li className="feature-item">
              <span className="feature-icon">✓</span>
              AI-powered image and text generation
            </li>
            <li className="feature-item">
              <span className="feature-icon">✓</span>
              Up to 100 working files
            </li>
            <li className="feature-item">
              <span className="feature-icon">✓</span>
              15GB of asset cloud storage
            </li>
            <li className="feature-item">
              <span className="feature-icon">✓</span>
              Extensive how-to guides
            </li>
          </ul>
        </div>
      </div>

      {/* Right Column - Signup Form */}
      <div className="signup-column">
        <div className="signup-card">
          <div className="signup-header">
            <h2>Get Started Absolutely Free</h2>
          </div>

          <div className="subscription-status">
            <h3>🚀 21-Day Pro Trial</h3>
            <p>
              Full access to all pro features for 21 days - no payment required
            </p>
          </div>

          <form onSubmit={handleTrialSignUp} className="signup-form">
            {error && <div className="error-message">{error}</div>}

            <div className="input-group">
              <input
                type="text"
                name="firstName"
                placeholder="First Name"
                value={formData.firstName}
                onChange={handleInputChange}
                required
                className="signup-input"
              />
            </div>

            <div className="input-group">
              <input
                type="email"
                name="email"
                placeholder="Email Address"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="signup-input"
              />
            </div>

            <div className="input-group">
              <input
                type="password"
                name="password"
                placeholder="Password"
                value={formData.password}
                onChange={handleInputChange}
                required
                className="signup-input"
              />
            </div>

            <div className="input-group">
              <input
                type="password"
                name="confirmPassword"
                placeholder="Confirm Password"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                required
                className="signup-input"
              />
            </div>

            <button
              type="submit"
              disabled={isAuthenticating}
              className="signup-button"
            >
              {isAuthenticating ? "Starting Trial..." : "START TRIAL"}
            </button>

            <div className="trial-terms">
              <p>
                By starting your trial, you agree to our terms of service. No
                credit card required.
              </p>
            </div>

            <div className="login-link">
              <span>Already have an account?</span>
              <br />
              <Link to="/login">Login here</Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TrialSignup;
